import PropTypes from 'prop-types';
import React, { PureComponent } from 'react';
import { Glyphicon, Overlay, Popover } from 'react-bootstrap';
import { connect } from 'react-redux';

import PaymentForm from '../PaymentForm';
import ConfirmDialog from './ConfirmDialog';
import EmailDialog from './EmailDialog';
import EventForm, { validateField } from './EventForm';
import Select from './Select';
import SendSmsForm from './SendSmsForm';
import { getNewId, validateTaskField } from './TaskEditor';
import TaskEditorField, { getDefaultField } from './TaskEditorField';

import {
    caseEditEvent,
    caseGetChargesAction,
    caseSetServerError,
    casesSetStatesAction
} from '../../actions/caseActions';
import {
    filterTasks,
    getFullName,
    getWorkflowTranslationHashTable,
    matterPartialFormSavePromise,
    transformTextFromPlaceholderMapping,
    updateTask
} from '../../helpers/utils';
import { isEmptyObject } from '../../validators/formValidators';


// Small per-row quick-action menu rendered after the checkbox in the cases
// table. The menu is rendered through a body-level portal (Overlay) so it is
// never clipped by the table's overflow and always sits on top of the rows.
// It only fires callbacks; the heavy dialogs live in a single shared
// CaseQuickActionDialogs instance (see below) to avoid mounting them per row.
//
export class QuickActionsMenu extends PureComponent {
    constructor(props) {
        super(props);
        this.toggle = this.toggle.bind(this);
        this.close = this.close.bind(this);
        this.state = { open: false };
    }

    toggle(evt) {
        evt.stopPropagation();
        this.setState((s) => ({ open: !s.open }));
    }

    close() {
        this.setState({ open: false });
    }

    item(glyph, label, onClick) {
        return (
            <div
                className="qa-item"
                style={{ padding: '5px 12px', cursor: 'pointer', whiteSpace: 'nowrap' }}
                onClick={(evt) => { evt.stopPropagation(); this.close(); onClick(); }}
            >
                {glyph && <Glyphicon glyph={glyph} style={{ marginRight: 8, width: 14 }} />}{label}
            </div>
        );
    }

    render() {
        const { row, onAction } = this.props;
        return (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                <span ref={(n) => { this.target = n; }} style={{ display: 'inline-flex' }}>
                    <a role="button"
                       title="Quick actions"
                       onClick={this.toggle}
                       style={{ cursor: 'pointer', padding: '0 4px', color: '#555' }}
                    >
                        <Glyphicon glyph="flash" />
                    </a>
                </span>
                <Overlay
                    show={this.state.open}
                    onHide={this.close}
                    rootClose
                    placement="right"
                    container={() => document.body}
                    target={() => this.target}
                >
                    <Popover id={`quick-actions-${row.id}`} style={{ zIndex: 2000 }}>
                        <div style={{ margin: '-9px -14px', minWidth: 160 }}>
                            {this.item('th-large', 'Set State…', () => onAction('state', row))}
                            {this.item('ok', 'Add Task', () => onAction('task', row))}
                            {this.item('comment', 'Add Event', () => onAction('event', row))}
                            {this.item('usd', 'Quick Bill', () => onAction('bill', row))}
                            {this.item('envelope', 'Send Email', () => onAction('email', row))}
                            {this.item('phone', 'Send SMS', () => onAction('sms', row))}
                        </div>
                    </Popover>
                </Overlay>
            </span>
        );
    }
}

QuickActionsMenu.propTypes = {
    row: PropTypes.object.isRequired,
    onAction: PropTypes.func.isRequired,
};


// Hosts the dialogs for the quick actions. A single instance is rendered by
// Cases; `action` / `row` select which dialog is open. The case is expected to
// already be loaded as the current case (state.cases.current) by the parent
// before `action` is set, so the reused forms pick it up from redux.
//
class CaseQuickActionDialogs extends PureComponent {
    constructor(props) {
        super(props);
        this.saveTask = this.saveTask.bind(this);
        this.saveEvent = this.saveEvent.bind(this);
        this.saveState = this.saveState.bind(this);
        this.state = {
            taskField: undefined,
            eventField: { EventType: 'Note' },
            caseState: '',
            loadingTask: false,
            loadingEvent: false,
            loadingState: false,
        };
    }

    componentWillReceiveProps(nextProps) {
        // Initialise the editable field when a dialog is (re)opened.
        //
        if (nextProps.action !== this.props.action && nextProps.action) {
            if (nextProps.action === 'task' && nextProps.currentCase && nextProps.currentConfig) {
                // Mirror CaseEdit's Add Task: prefill Title/Description from the
                // case's workflow placeholder mapping (e.g. the client name).
                //
                const placeholderMapping = getWorkflowTranslationHashTable(nextProps.currentConfig, nextProps.currentCase, {});
                const newTask = getDefaultField(
                    getNewId(nextProps.allTasks || []),
                    nextProps.loggedIn && nextProps.loggedIn.id,
                    nextProps.currentCase.id);
                newTask['Title'] = transformTextFromPlaceholderMapping(newTask['Title'] || '', placeholderMapping);
                newTask['Description'] = transformTextFromPlaceholderMapping(newTask['Description'] || '', placeholderMapping);
                this.setState({ taskField: newTask });
            } else if (nextProps.action === 'event') {
                this.setState({ eventField: { EventType: 'Note' } });
            } else if (nextProps.action === 'state') {
                const current = (nextProps.row && nextProps.row['notes'] && nextProps.row['notes']['CaseState']) || '';
                this.setState({ caseState: current });
            }
        }
    }

    saveTask(_task) {
        const { currentCase, dispatch, onClose } = this.props;
        if (!currentCase || !currentCase.id) {
            return;
        }

        const task = { ..._task, '$Modified$': true, CaseId: currentCase.id };
        if (!validateTaskField(task)) {
            this.setState({ taskField: task });
            return;
        }

        this.setState({ loadingTask: true });
        updateTask(task, this.props, () => {
            this.setState({ loadingTask: false });
            onClose();
        }, (error) => {
            console.error(new Error().stack, error);
            this.setState({ loadingTask: false });
            dispatch(caseSetServerError(error));
        });
    }

    saveState() {
        const { row, dispatch, onClose } = this.props;
        if (!row || !this.state.caseState) {
            return;
        }

        this.setState({ loadingState: true });
        dispatch(casesSetStatesAction([row.id], this.state.caseState))
            .then(() => {
                this.setState({ loadingState: false });
                onClose();
            })
            .catch((error) => {
                console.error(new Error().stack, error);
                this.setState({ loadingState: false });
                dispatch(caseSetServerError(error));
            });
    }

    saveEvent(_event) {
        const { currentCase, dispatch, onClose } = this.props;
        if (!currentCase || !currentCase.id) {
            return;
        }

        const event = validateField(_event);
        if (!isEmptyObject(event.error)) {
            this.setState({ eventField: event });
            return;
        }

        event['CaseId'] = currentCase.id;
        event['$CaseId$'] = currentCase.id;
        if (event['EventType'] === 'Charge') {
            event['amount'] = Math.trunc((event['amount'] || 0) * 100);
        } else if (event['EventType'] === 'ChargeRefund') {
            event['amount'] = -Math.trunc((event['amount'] || 0) * 100);
            event['EventType'] = 'Charge';
        } else {
            delete event['amount'];
        }

        if (event['EventType'] !== 'Time') {
            delete event['time'];
            delete event['hourlyRate'];
        }

        if (event['EventType'] !== 'Cost') {
            delete event['items'];
        }

        this.setState({ loadingEvent: true });
        const done = () => {
            this.setState({ loadingEvent: false, eventField: { EventType: 'Note' } });
            onClose();
        };
        const fail = (error) => {
            console.error(new Error().stack, error);
            this.setState({ loadingEvent: false });
            dispatch(caseSetServerError(error));
        };

        dispatch(caseEditEvent(event))
            .then(() => {
                if (event['EventType'] === 'Charge') {
                    dispatch(caseGetChargesAction(currentCase.id)).then(done).catch(fail);
                } else if (event['EventType'] === 'Time') {
                    matterPartialFormSavePromise(dispatch, parseInt(currentCase.id, 10), currentCase)
                        .then(done).catch(fail);
                } else {
                    done();
                }
            })
            .catch(fail);
    }

    // Append the case's client name and type to a dialog title, e.g.
    // "Add Task — Ana Ludena Suárez (Bankruptcy)". Uses the loaded case when
    // available, otherwise the table row (e.g. the Set State dialog).
    //
    dialogTitle(base) {
        const matchesRow = this.props.currentCase && this.props.row &&
            this.props.currentCase.id === this.props.row.id;
        const src = matchesRow ? this.props.currentCase : (this.props.row || {});
        const name = getFullName(src.client);
        const type = src['CaseType'] || (src.intake && src.intake['CaseType']) || '';
        let context = name || '';
        if (type) {
            context = context ? `${context} (${type})` : type;
        }

        return context ? `${base} — ${context}` : base;
    }

    render() {
        const { action, currentCase, caseTasks, loadingCurrent, onClose } = this.props;
        if (!action) {
            return null;
        }

        const caseType = currentCase && currentCase['CaseType'];

        return (
            <React.Fragment>
            {action === 'task' && this.state.taskField && (
                <ConfirmDialog title={this.dialogTitle('Add Task')} open cancel={onClose}>
                    <TaskEditorField
                        initialField={this.state.taskField}
                        disabled={loadingCurrent || this.state.loadingTask}
                        tasks={caseTasks}
                        disableMove
                        disableCheckBox
                        editMode
                        useSimpleDateTime
                        renderGoToCaseButton={false}
                        isEmptyChronoError={false}
                        onChange={(taskField) => this.setState({ taskField })}
                        label="Add New Task"
                        actionLabel="Add Task"
                        caseType={caseType}
                        currentCase={currentCase}
                        onActionBtnClicked={this.saveTask}
                    />
                </ConfirmDialog>
            )}

            {action === 'event' && (
                <ConfirmDialog title={this.dialogTitle('Add Event')} open cancel={onClose}>
                    <EventForm
                        initialField={this.state.eventField}
                        onChange={(eventField) => this.setState({ eventField })}
                        label="Add New Event"
                        disabled={this.state.loadingEvent || loadingCurrent || !currentCase || !currentCase.id}
                        actionLabel="Add Event"
                        onActionBtnClicked={this.saveEvent}
                    />
                </ConfirmDialog>
            )}

            {action === 'bill' && (
                <ConfirmDialog title={this.dialogTitle('Quick Bill / Payment')} open cancel={onClose} size="tall">
                    <PaymentForm loadingCurrent={loadingCurrent} />
                </ConfirmDialog>
            )}

            {action === 'email' && (
                <EmailDialog
                    title={this.dialogTitle('Send Email')}
                    open
                    cancel={onClose}
                />
            )}

            {action === 'sms' && (
                <SendSmsForm
                    showAsDialog
                    open
                    cancel={onClose}
                    onSmsSent={onClose}
                    title={this.dialogTitle('Send SMS')}
                    templatesFieldName="notesSmsTemplates"
                />
            )}

            {action === 'state' && (
                <ConfirmDialog
                    title={this.dialogTitle('Set Case State')}
                    open
                    cancel={onClose}
                    confirm={this.saveState}
                    confirmLabel="Set State"
                    disabled={this.state.loadingState || !this.state.caseState}
                >
                    <Select
                        field={{
                            name: 'caseState',
                            value: this.state.caseState,
                            onChange: (evt) => this.setState({ caseState: evt.target.value }),
                        }}
                        label="Case State"
                        name="caseState"
                        options={this.props.states.map((s) => ({ label: s['type'], value: s['type'] }))}
                    />
                </ConfirmDialog>
            )}
            </React.Fragment>
        );
    }
}

CaseQuickActionDialogs.propTypes = {
    action: PropTypes.string,
    row: PropTypes.object,
    onClose: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => {
    const currentCase = state.cases.current;
    const config = state.user.config;
    return {
        currentCase,
        currentConfig: config,
        loadingCurrent: state.cases.loadingCurrent,
        loggedIn: state.user.loggedIn,
        states: (config && config['caseStates']) || [],
        allTasks: state.user.tasks || [],
        caseTasks: filterTasks(state.user.tasks, currentCase && currentCase.id, false),
    };
};

export default connect(mapStateToProps)(CaseQuickActionDialogs);
